import { ComparableBoatData, useGetActiveCarouselItem } from '@components/CompareBoats/hooks/useCompare';
import { Button, Grid, Tooltip } from 'antd';
import { KeenSliderPlugin, useKeenSlider } from 'keen-slider/react';
import { Info } from 'lucide-react';
import { FC, useEffect } from 'react';
import styles from '../CompareTable.module.scss';
import PanelDataItem from './PanelDataItem.component';

interface GeneralSpecsPanelDataInterface {
  boatComparableData: ComparableBoatData[];
}

const MutationPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      slider.update();
    });
  });

  const config = { childList: true };

  slider.on('created', () => {
    observer.observe(slider.container, config);
  });
  slider.on('destroyed', () => {
    observer.disconnect();
  });
};

const GeneralSpecsPanelData: FC<GeneralSpecsPanelDataInterface> = ({ boatComparableData }) => {
  const activeCarouselItem = useGetActiveCarouselItem();
  const { md } = Grid.useBreakpoint();

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      drag: false,
      slides: {
        perView: 7,
      },
      breakpoints: {
        '(max-width: 1440px)': {
          slides: { perView: 6 },
        },
        '(max-width: 1200px)': {
          slides: { perView: 5 },
        },
        '(max-width: 1024px)': {
          slides: { perView: 4 },
        },
        '(max-width: 992px)': {
          slides: { perView: 3 },
        },
        '(max-width: 768px)': {
          slides: { perView: 2 },
        },
        '(max-width: 520px)': {
          slides: { perView: 2 },
        },
      },
    },
    [MutationPlugin],
  );

  useEffect(() => {
    instanceRef?.current?.moveToIdx(activeCarouselItem);
  }, [activeCarouselItem, instanceRef]);

  const mainSpecsList: { specLabel: string; specId: number; noDataSpec?: boolean; specTooltip?: string }[] = [
    { specLabel: 'Top speed', specId: 16 },
    { specLabel: 'Cruising speed', specId: 17 },
    { specLabel: 'Mast height', specId: 18 },
    { specLabel: 'Length overall (LOA)', specId: 19 },
    { specLabel: 'Length at waterline (LWL)', specId: 20 },
    { specLabel: 'Beam overall', specId: 21 },
    { specLabel: 'Beam center-line', specId: 22 },
    { specLabel: 'Beam hull', specId: 23 },
    { specLabel: 'Headroom', specId: 24 },
    { specLabel: 'Bridgedeck clearance', specId: 25 },
    { specLabel: 'Nominal bridgedeck clearance', specId: 26 },
    { specLabel: 'Draft maximum', specId: 27 },
    { specLabel: 'Draft minimum', specId: 28 },
    { specLabel: 'Displacement', specId: 29 },
    { specLabel: 'Tonnage Gross (DWT)', specId: 30 },
    { specLabel: 'Tonnage Net (DWT)', specId: 31 },
    { specLabel: 'Ballast weight', specId: 32 },
    { specLabel: 'Ballast type', specId: 33 },
    { specLabel: 'Helm', specId: 34 },
    { specLabel: 'Rudder', specId: 35 },
    { specLabel: 'Hull appendages', specId: 36, noDataSpec: true },
    { specLabel: 'Hull appendages A', specId: 37 },
    { specLabel: 'Hull appendages B', specId: 38 },
    { specLabel: 'Cockpit', specId: 39 },
    { specLabel: 'Unsinkable', specId: 40 },
    { specLabel: 'Trailerable', specId: 41 },
    { specLabel: 'CE classification', specId: 42, noDataSpec: true },
    { specLabel: 'CE classification A', specId: 43 },
    { specLabel: 'CE classification B', specId: 44 },
    { specLabel: 'CE classification C', specId: 45 },
    { specLabel: 'CE classification D', specId: 46 },
  ];

  return (
    <div className={styles.specsPanelDataContainer}>
      <div style={md ? { width: 192 } : { width: '100%' }}>
        {mainSpecsList.map((mainSpecItem) => (
          <div key={mainSpecItem.specId}>
            <div
              className={mainSpecItem?.noDataSpec ? styles.noDataSpecsNamesStyles : styles.specsNamesStyles}
              key={mainSpecItem.specId}
            >
              {mainSpecItem.specLabel}
              {mainSpecItem?.specTooltip && (
                <Tooltip title={mainSpecItem?.specTooltip}>
                  <Button icon={<InfoCircleOutlined />} shape="circle" className={styles.tooltipInfoButton} />
                </Tooltip>
              )}
            </div>
            {mainSpecItem?.specId === 16 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.topSpeed} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 17 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.cruisingSpeed} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 18 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.mast} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 19 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.loa} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 20 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.lwl} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 21 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.boa} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 22 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.bcl} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 23 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.bh} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 24 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.headroom} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 25 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.bdcl} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 26 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.nom} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 27 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.draftMax} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 28 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.draftMin} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 29 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.displacement} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 30 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.tonnageGross} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 31 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.tonnageNet} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 32 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.ballastWeight} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 33 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.ballastType} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 34 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.helm} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 35 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.rudder} key={boat?._id} panelDataType="text" />
              ))}

            {mainSpecItem?.specId === 37 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.hullAppendages?.at(0)?.name} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 38 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.hullAppendages?.at(1)?.name} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 39 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.cockpitType} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 40 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.unsinkable} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 41 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.trailerable} key={boat?._id} panelDataType="text" />
              ))}

            {mainSpecItem?.specId === 43 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.ceClassification?.a?.level} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 44 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.ceClassification?.b?.level} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 45 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.ceClassification?.c?.level} key={boat?._id} panelDataType="text" />
              ))}
            {mainSpecItem?.specId === 46 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.ceClassification?.d?.level} key={boat?._id} panelDataType="text" />
              ))}
          </div>
        ))}
      </div>

      {md && (
        <div ref={ref} className="keen-slider">
          {boatComparableData?.map((boat) => (
            <div key={boat._id} className="keen-slider__slide">
              <PanelDataItem panelData={boat?.topSpeed} panelDataType="text" />
              <PanelDataItem panelData={boat?.cruisingSpeed} panelDataType="text" />
              <PanelDataItem panelData={boat?.mast} panelDataType="text" />
              <PanelDataItem panelData={boat?.loa} panelDataType="text" />
              <PanelDataItem panelData={boat?.lwl} panelDataType="text" />
              <PanelDataItem panelData={boat?.boa} panelDataType="text" />
              <PanelDataItem panelData={boat?.bcl} panelDataType="text" />
              <PanelDataItem panelData={boat?.bh} panelDataType="text" />
              <PanelDataItem panelData={boat?.headroom} panelDataType="text" />
              <PanelDataItem panelData={boat?.bdcl} panelDataType="text" />
              <PanelDataItem panelData={boat?.nom} panelDataType="text" />
              <PanelDataItem panelData={boat?.draftMax} panelDataType="text" />
              <PanelDataItem panelData={boat?.draftMin} panelDataType="text" />
              <PanelDataItem panelData={boat?.displacement} panelDataType="text" />
              <PanelDataItem panelData={boat?.tonnageGross} panelDataType="text" />
              <PanelDataItem panelData={boat?.tonnageNet} panelDataType="text" />
              <PanelDataItem panelData={boat?.ballastWeight} panelDataType="text" />
              <PanelDataItem panelData={boat?.ballastType} panelDataType="text" />
              <PanelDataItem panelData={boat?.helm} panelDataType="text" />
              <PanelDataItem panelData={boat?.rudder} panelDataType="text" ellipsisMaxLength={40} />
              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.hullAppendages?.at(0)?.name} panelDataType="text" />
              <PanelDataItem panelData={boat?.hullAppendages?.at(1)?.name} panelDataType="text" />
              <PanelDataItem panelData={boat?.cockpitType} panelDataType="text" />
              <PanelDataItem panelData={boat?.unsinkable} panelDataType="text" />
              <PanelDataItem panelData={boat?.trailerable} panelDataType="text" />
              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.ceClassification?.a?.level} panelDataType="text" />
              <PanelDataItem panelData={boat?.ceClassification?.b?.level} panelDataType="text" />
              <PanelDataItem panelData={boat?.ceClassification?.c?.level} panelDataType="text" />
              <PanelDataItem panelData={boat?.ceClassification?.d?.level} panelDataType="text" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GeneralSpecsPanelData;
